import { useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';

/**
 * Accordion Component (Preline UI)
 *
 * Expandable/collapsible content container with header and body.
 * Follows Preline design patterns with Tailwind CSS.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.header - Content to display in accordion header (always visible)
 * @param {React.ReactNode} props.children - Content to display when expanded
 * @param {boolean} props.defaultExpanded - Whether accordion starts expanded (default: false)
 * @param {string} props.className - Additional CSS classes for container
 */
export function Accordion({ header, children, defaultExpanded = false, className = '' }) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`mb-2 ${className}`}>
      {/* Accordion Header - Always Visible */}
      <div
        onClick={toggleExpanded}
        className={`
          flex items-center justify-between
          py-2 px-3
          bg-app border border-border-default
          cursor-pointer hover:bg-brand/3 transition-colors
          ${isExpanded ? 'rounded-t-md border-b-0' : 'rounded-md'}
        `}
      >
        {/* Header Content */}
        <div className="flex-1 min-w-0 overflow-hidden">{header}</div>

        {/* Expand/Collapse Icon */}
        <Icon
          icon={isExpanded ? 'lucide:chevron-up' : 'lucide:chevron-down'}
          className="size-4 text-subtle ml-2 transition-transform flex-shrink-0"
        />
      </div>

      {/* Accordion Body - Conditionally Visible */}
      {isExpanded && (
        <div className="py-2 px-3 bg-app border border-border-default border-t-0 rounded-b-md overflow-hidden break-words">
          {children}
        </div>
      )}
    </div>
  );
}

Accordion.propTypes = {
  header: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  defaultExpanded: PropTypes.bool,
  className: PropTypes.string,
};
